import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState, useRef } from "react";
import { Button, Container, Row, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  EnvironmentVariableInput,
  ActionType as ExecutionActionType,
  Job,
  RepoObject as RepoObjectEE
} from "../../../backend/executionTypes";
import {
  generateDateToLocalTimeZone,
  getActionTypeKeyFromValue,
  getQualityCheckStatusKeyFromValue
} from "../../../helpers/stringHelper";
import { ActionTypeUI, RepositoryTree } from "../../../backend/types";
import { useUserHelper } from "../../../hooks/userHelper";
import Error from "../error";
import ErrorBoundary from "../errorBoundary";
import { FileSelectModal } from "../fileSelectModal";
import { FileType } from "../fileTreeView";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { ApolloError } from "@apollo/client/errors";
import { LazyQueryExecFunction, OperationVariables } from "@apollo/client";
import { useCreateJobHook } from "./createJobHooks";
import { EnvironmentSelect } from "./environmentSelect";
import {
  getFileNameAndRepoName,
  getRepoObjectForExecution
} from "./fileHelper";
import { setExecutionSettingRecommendation } from "./recommendationHelper";
import { Environment } from "../../../backend/interactiveTypes";
import {
  QualityCheckStatusUI,
  QualityCheckStatusType
} from "../../../backend/types";
import Success from "../success";
import UserManualLink from "../usermanual";
import {
  filterTopFolderFromSelectedFiles,
  recursivelyGetAllFilesInFolder
} from "../../../helpers/treeHelper";

const timeConversion = 86400;
const memoryConversion = 1024;

export interface ExtendRepoObjects extends RepoObjectEE {
  absolutePath?: string;
  notFound?: boolean;
}

export function CreateJob(props: {
  executionFileSource: string;
  job?: Job;
  parentLoading?: boolean[];
  parentError?: ApolloError[];
  getFilesForSelection?: LazyQueryExecFunction<any, OperationVariables>;
  setExecutionFileSource?: React.Dispatch<React.SetStateAction<string>>;
  interactiveEnvironments?: Environment[];
  IEEEBridge?: boolean;
  repos?: RepositoryTree[];
}) {
  const {
    activityData,
    activityId,
    callRepositoryObjects,
    isGetRepositoryObjectsLoading,
    getRepositoryObjectsError,
    callSingleRepositoryObject,
    isGetRepositoryObjectLoading,
    getRepositoryObjectError,
    executionImages,
    isLoadingGetExecutionImages,
    errorGetExecutionImages,
    submitJobFromIE, // interactive
    isSubmitExecutionJobIELoading,
    errorSubmitExecutionJobIE,
    getExecIEImageRecommendations,
    isLoadingExecIEImageRecommendations,
    errrorExecIEImageRecommendations,
    getExecInputIERecommendations,
    isLoadingExecInputIERecommendations,
    errrorExecInputIERecommendations,
    submitJob, //execution
    isSubmitExecutionJobLoading,
    errorSubmitExecutionJob,
    getExecutionImageRecommendations,
    isLoadingExecutionImageRecommendations,
    errrorExecutionImageRecommendations,
    getExecutionInputRecommendations,
    isLoadingExecutionInputRecommendations,
    errrorExecutionInputRecommendations,
    refreshToken
  } = useCreateJobHook();

  const history = useHistory();
  const { isBayerUser } = useUserHelper();
  const [tree, setTree] = useState<any>(props.repos);
  const [executionName, setExecutionName] = useState<string>("");
  const [executionDescription, setExecutionDescription] = useState<string>("");
  const [executionImage, setExecutionImage] = useState<{
    id: string;
    notLatest?: boolean;
  }>({ id: "" });
  const [executionScript, setExecutionScript] = useState<ExtendRepoObjects>({
    repositoryId: "",
    versionId: "",
    absolutePath: ""
  });
  const [executionInputs, setExecutionInputs] = useState<any>([]);
  const [executionWorkingDir, setExecutionWorkingDir] = useState<string>("/");
  const [executionActionType, setExecutionActionType] = useState(
    ActionTypeUI.MODEL_EXECUTION
  );
  const [qualityCheckStatus, setQualityCheckStatus] =
    useState<QualityCheckStatusUI>(QualityCheckStatusUI.NOT_STARTED);
  const [executionTimeout, setExecutionTimeout] = useState<number>(14);
  const [nodes, setNodes] = useState<number>(1);
  const [vCPU, setVCPU] = useState<number>(4);
  const [memory, setMemory] = useState<number>(4);
  const temporaryValue = useRef<any>();
  const [modalConfig, setModalConfig] = useState<{
    open: boolean;
    fileType?: FileType;
    text?: string | any;
    location?: string;
  }>({
    open: false
  });
  const [customCommand, setCustomCommand] = useState<string>("");
  const [environmentVariables, setEnvironmentVariables] = useState<
    EnvironmentVariableInput[] | []
  >([]);
  const [environmentalVariableName, setEnvironmentalVariableName] =
    useState<string>("");
  const [environmentalVariableValue, setEnvironmentalVariableValue] =
    useState<string>("");
  const [
    isLoadingRepositoryObjectsPagination,
    setIsLoadingRepositoryObjectsPagination
  ] = useState(false);
  const [
    isLoadingValidateRepositoryObjects,
    setIsLoadingValidateRepositoryObjects
  ] = useState(false);

  const defaultIsCustomCommandVisible =
    localStorage.getItem("defaultIsCustomCommandVisible") &&
    localStorage.getItem("defaultIsCustomCommandVisible") === "true"
      ? Boolean(localStorage.getItem("defaultIsCustomCommandVisible"))
      : false;
  const [isCustomCommandVisible, setIsCustomCommandVisible] = useState(
    defaultIsCustomCommandVisible
  );
  const [hasFetch, setHasFetch] = useState<boolean>(false);
  const [cloneAlreadyRecommended, setCloneAlreadyRecommended] =
    useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const isFetchingInputs = useRef(false);

  const getImageById = (imageId: string) => {
    return executionImages?.find((x: any) => x.id === imageId);
  };

  const getCommandRecommendation = (executionImageId: string): string => {
    const recommendations = getImageById(executionImageId)?.recommendations;
    let command: string;
    if (recommendations?.length == 1) {
      command = recommendations[0].command;
    } else {
      command = recommendations?.find((recommendation: any) => {
        return recommendation.isMultinode === false;
      }).command;
    }

    return command;
  };

  const updateFileSelectionTemporary = (
    type: FileType,
    node: ExtendRepoObjects
  ) => {
    if (type === FileType.Script) {
      temporaryValue.current = {
        repositoryId: node.repositoryId,
        versionId: node.versionId,
        absolutePath: node.absolutePath
      };
    } else if (type === FileType.Input) {
      temporaryValue.current = node;
    } else if (type === FileType.WorkingDir) {
      temporaryValue.current = node;
    }
  };

  const openModal = (fileType: FileType) => {
    if (props.executionFileSource !== "phil" && props.getFilesForSelection) {
      props?.getFilesForSelection({
        variables: {
          environmentId: props.executionFileSource
        }
      });
    }
    if (fileType === FileType.Input) {
      setModalConfig({
        open: true,
        fileType,
        text: "Select Input Files"
      });
    } else if (fileType === FileType.WorkingDir) {
      setModalConfig({
        open: true,
        fileType,
        text: "Select Working Directory"
      });
    } else if (fileType === FileType.Script) {
      setModalConfig({
        open: true,
        fileType,
        text: "Select Your Script File"
      });
    }
  };

  // get missing repository objects when user selects folder but it didn't finish recursively fetching all files in the folder
  const getMissingRepositoryObjectsByFolder = async () => {
    setIsLoadingRepositoryObjectsPagination(true);

    const selected = Array.isArray(temporaryValue.current)
      ? temporaryValue.current
      : temporaryValue.current
        ? [temporaryValue.current]
        : [];
    const selectedFiles = selected.filter(
      (item: any) => !item.absolutePath?.endsWith("/")
    );
    const selectedFolders = selected.filter(
      (item: any) => item.absolutePath?.endsWith("/")
    );

    if (selectedFolders.length === 0) {
      setIsLoadingRepositoryObjectsPagination(false);
      return selectedFiles;
    }

    const folders = await filterTopFolderFromSelectedFiles(selectedFolders);
    const filesFromFolders = await recursivelyGetAllFilesInFolder(
      folders,
      callRepositoryObjects
    );

    setIsLoadingRepositoryObjectsPagination(false);
    return [
      ...selectedFiles,
      ...filesFromFolders.map((file: any) => ({
        ...file,
        repositoryId: file.repository.id
      }))
    ];
  };

  const calculateInputFiles = async () => {
    const files =
      props.executionFileSource === "phil"
        ? await getMissingRepositoryObjectsByFolder()
        : Array.isArray(temporaryValue.current)
          ? temporaryValue.current
          : temporaryValue.current
            ? [temporaryValue.current]
            : [];
    setExecutionInputs((prevState: any) => {
      const existingPaths = new Set(
        prevState.map((item: any) => item.absolutePath)
      );
      const uniqueNewFiles = files.filter((file: any) => {
        if (existingPaths.has(file.absolutePath)) {
          return false;
        }
        existingPaths.add(file.absolutePath);
        return true;
      });
      return [...prevState, ...uniqueNewFiles];
    });
  };

  const onConfirmModal = () => {
    if (modalConfig.fileType === FileType.Script) {
      if (temporaryValue.current) {
        setExecutionScript(temporaryValue.current);

        if (temporaryValue.current.absolutePath) {
          const stripWorkingDirectory: any =
            temporaryValue.current.absolutePath.split("/");
          stripWorkingDirectory.pop();
          setExecutionWorkingDir(`${stripWorkingDirectory.join("/")}/`);
        }

        if (props.executionFileSource === "phil") {
          setExecutionSettingRecommendation(
            !props.job?.id,
            props.executionFileSource,
            setExecutionImage,
            setCustomCommand,
            setExecutionInputs,
            getCommandRecommendation,
            getExecutionImageRecommendations,
            getExecutionInputRecommendations,
            {
              activityId: activityId,
              entrypoint: {
                versionId: temporaryValue.current.versionId,
                repositoryId: temporaryValue.current.repositoryId
              }
            },
            activityData,
            callRepositoryObjects
          );
        } else {
          setExecutionSettingRecommendation(
            !props.job?.id,
            props.executionFileSource,
            setExecutionImage,
            setCustomCommand,
            setExecutionInputs,
            getCommandRecommendation,
            getExecIEImageRecommendations,
            getExecInputIERecommendations,
            {
              environmentId: props.executionFileSource,
              path: temporaryValue.current.absolutePath
            },
            activityData
          );
        }
      }
    } else if (modalConfig.fileType === FileType.Input) {
      temporaryValue.current && calculateInputFiles();
    } else if (modalConfig.fileType === FileType.WorkingDir) {
      temporaryValue.current && setExecutionWorkingDir(temporaryValue.current);
    }
    temporaryValue.current = "";
    setModalConfig({ open: false });
  };

  const getFileSelection = () => {
    if (modalConfig.fileType === FileType.Script) {
      return executionScript["absolutePath"];
    } else if (modalConfig.fileType === FileType.Input) {
      return executionInputs;
    } else if (modalConfig.fileType === FileType.WorkingDir) {
      return executionWorkingDir;
    }
  };

  const addEnvironmentalVariablesEntry = () => {
    setEnvironmentVariables([
      ...environmentVariables,
      {
        name: environmentalVariableName,
        value: environmentalVariableValue
      }
    ]);
    setEnvironmentalVariableName("");
    setEnvironmentalVariableValue("");
  };

  const handleDeleteEnvironmentalVariableChip = (name: String) => {
    setEnvironmentVariables(
      environmentVariables.filter((parameter: any) => parameter.name !== name)
    );
  };

  const handleDeleteChip = (type: string, filterObject?: string) => {
    if (type === "script") {
      setExecutionScript({ repositoryId: "", versionId: "", absolutePath: "" });
    } else if (type === "input") {
      setExecutionInputs(
        executionInputs.filter(
          (input: any) => input.absolutePath !== filterObject
        )
      );
    } else if (type === "workingDir") {
      setExecutionWorkingDir("");
    }
  };

  const resetJob = () => {
    setExecutionName("");
    setExecutionDescription("");
    setExecutionImage({ id: "" });
    setExecutionScript({ repositoryId: "", versionId: "", absolutePath: "" });
    setExecutionInputs([]);
    setExecutionWorkingDir(`${activityData?.mainRepository?.name}/`);
    setExecutionActionType(ActionTypeUI.ADF_CREATION);
    setQualityCheckStatus(QualityCheckStatusUI.NOT_STARTED);
    setExecutionTimeout(14);
    setNodes(1);
    setCustomCommand("");
    setEnvironmentVariables([]);
  };

  const isFileHiddenInTreeView = (file: any) => {
    const fileType = modalConfig.fileType;
    if (fileType === FileType.Script) {
      return file.absolutePath.endsWith("/");
    }
    return !file.absolutePath.endsWith("/");
  };

  const createExecutionJob = () => {
    const submitJobInput = {
      name: executionName,
      imageId: executionImage.id,
      activityId: activityData.id,
      actionType: getActionTypeKeyFromValue(executionActionType),
      qualityCheckStatus: getQualityCheckStatusKeyFromValue(qualityCheckStatus),
      workingDir: executionWorkingDir,
      description: executionDescription,
      command: customCommand,
      environmentVariables: environmentVariables,
      timeout: executionTimeout * timeConversion,
      memory: memory * memoryConversion,
      vCPU: vCPU,
      numOfNodes: getImageById(executionImage.id)?.recommendations.some(
        (e: { isMultinode: boolean }) => e.isMultinode
      )
        ? nodes
        : 1,
      ...(props.job?.id && { parentJobId: props.job?.id })
    };
    if (props.executionFileSource !== "phil") {
      const [repoName, fileName] = getFileNameAndRepoName(
        executionScript.absolutePath
      );

      submitJobFromIE({
        variables: {
          ...submitJobInput,
          refreshToken: refreshToken,
          environmentId: props.executionFileSource,
          entrypoint: { repoName: repoName, path: fileName },
          inputs: executionInputs
            .filter(
              (input: any) =>
                input.absolutePath !== executionScript.absolutePath
            )
            .map((input: any) => {
              const [repoName, fileName] = getFileNameAndRepoName(
                input.absolutePath
              );

              return {
                repoName: repoName,
                path: fileName
              };
            })
        }
      }).then(() => {
        setSuccessMessage(
          "Interactive Execution Bridge Job Created. It can take up to 15 Minutes to appear."
        );
        history.push({
          pathname: `/activity/${activityId}/executions`,
          state: { createdIEEEBridge: true }
        });
      });
    } else {
      submitJob({
        variables: {
          ...submitJobInput,
          refreshToken: refreshToken,
          entrypoint: {
            repositoryId: executionScript.repositoryId,
            versionId: executionScript.versionId
          },
          inputs: executionInputs
            .filter(
              (x: any) =>
                x.versionId !== "" &&
                x.absolutePath !== executionScript.absolutePath
            )
            .map((x: any) => ({
              repositoryId: x.repositoryId,
              versionId: x.versionId
            }))
        }
      }).then(({ data }) => {
        history.push({
          pathname: `/activity/${activityId}/execution/${data.submitJob.id}`,
          state: data.submitJob
        });
      });
    }
  };

  const handleImageChange = (value: string) => {
    setExecutionImage({ id: value });
    setNodes(1);

    if (!props.job?.id) {
      setCustomCommand(getCommandRecommendation(value));
    }
  };

  const handleOnNodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number(event.target.value) > 0 && Number(event.target.value) <= 32) {
      setNodes(Number(event.target.value));
      let command: string;
      const recommendations = getImageById(executionImage.id).recommendations;

      if (Number(event.target.value) > 1) {
        command = command = recommendations.find((recommendation: any) => {
          return recommendation.isMultinode === true;
        }).command;
      } else {
        command = recommendations.find((recommendation: any) => {
          return recommendation.isMultinode === false;
        }).command;
      }

      setCustomCommand(command);
    }
  };

  const isLoadingConditions = [
    !activityData,
    isGetRepositoryObjectsLoading,
    isLoadingGetExecutionImages,
    isSubmitExecutionJobLoading,
    isSubmitExecutionJobIELoading,
    isLoadingExecIEImageRecommendations,
    isLoadingExecutionInputRecommendations,
    isLoadingExecutionImageRecommendations,
    isLoadingExecInputIERecommendations,
    isGetRepositoryObjectLoading,
    isLoadingRepositoryObjectsPagination,
    isLoadingValidateRepositoryObjects,
    ...(props.parentLoading ?? [])
  ];
  const isLoading = isLoadingConditions.some((condition) => condition);

  const errorConditions = [
    getRepositoryObjectsError,
    errorGetExecutionImages,
    errorSubmitExecutionJob,
    errorSubmitExecutionJobIE,
    errrorExecIEImageRecommendations,
    errrorExecutionInputRecommendations,
    errorGetExecutionImages,
    errrorExecutionImageRecommendations,
    errrorExecInputIERecommendations,
    getRepositoryObjectError,
    ...(props.parentError ?? [])
  ];
  const error = errorConditions.find((condition) => condition);

  const disableCreateButton = () => {
    return (
      executionName === "" ||
      executionImage.id === "" ||
      (props.executionFileSource === "phil" &&
        executionScript.absolutePath === "") ||
      (props.executionFileSource !== "phil" &&
        executionScript.absolutePath === "") ||
      (props.executionFileSource !== "phil" &&
        executionScript.absolutePath === null) ||
      customCommand === "" ||
      isSubmitExecutionJobLoading ||
      isSubmitExecutionJobIELoading ||
      executionScript.notFound ||
      isLoading ||
      executionInputs.some((input: ExtendRepoObjects) => input.notFound)
    );
  };

  useEffect(() => {
    if (props.executionFileSource !== "phil") {
      setTree(props.repos);
    } else {
      setTree([]);
    }
  }, [props.repos]);

  useEffect(() => {
    localStorage.setItem(
      "defaultIsCustomCommandVisible",
      String(isCustomCommandVisible)
    );
    if (!isCustomCommandVisible && executionImage) {
      setCustomCommand(getCommandRecommendation(executionImage.id));
    }
  }, [isCustomCommandVisible]);

  const readyForFetchRepositoryObject =
    (tree && tree.length > 0) || props.executionFileSource === "phil";

  useEffect(() => {
    // called on component mount and prepopulate
    // if failure to prepopulate file source, it will run once more
    if (props.job?.id && hasFetch === false) {
      if (readyForFetchRepositoryObject) {
        fetchScript(tree, props.job?.entrypoint);
        fetchInputs(tree, props.job?.inputs);
        setExecutionWorkingDir(props.job?.workingDir);

        setHasFetch(true);
      } else {
        const command = props.job?.userCommand
          ? props.job?.userCommand
          : getCommandRecommendation(props.job?.image.id);

        setExecutionName(props.job?.name);
        setVCPU(props.job?.vCPU);
        setMemory(props.job?.memory / memoryConversion);
        setNodes(props.job?.numOfNodes);
        setCustomCommand(command);
        setExecutionActionType(ActionTypeUI[props.job?.actionType]);
        setExecutionDescription(props.job?.description ?? "");
        if (props.job?.qualityCheckStatus) {
          setQualityCheckStatus(
            QualityCheckStatusUI[
              props.job?.qualityCheckStatus as unknown as QualityCheckStatusType
            ]
          );
        }
        if (props.job?.timeout) {
          setExecutionTimeout(props.job?.timeout / timeConversion);
        }
        if (props.job?.environmentVariables) {
          setEnvironmentVariables(
            props.job?.environmentVariables as EnvironmentVariableInput[]
          );
        }
        setExecutionImage({ id: props.job?.image.id });
      }
    }
  }, [
    props.job,
    tree,
    props.parentLoading?.every((loading) => loading === false)
  ]);

  useEffect(() => {
    if (executionScript.absolutePath) {
      const fileName = executionScript.absolutePath.split("/").pop();
      setExecutionName(fileName + " " + generateDateToLocalTimeZone());
    }

    // image check only for copy run, all other cases are checked in the modal
    // we can only identiy the latest image by using the script versionId from the recommendation api
    // if script does not exist, there is no recommendation. default to the image from the clone job
    if (modalConfig.open === false && props.job?.id) {
      if (executionScript.notFound === true) {
        setExecutionImage({ id: props.job.image.id, notLatest: false });
      } else if (
        executionScript.notFound === false &&
        props.executionFileSource === "phil" &&
        cloneAlreadyRecommended === false
      ) {
        getExecutionImageRecommendations({
          variables: {
            activityId: activityId,
            entrypoint: {
              repositoryId: executionScript?.repositoryId,
              versionId: executionScript?.versionId
            }
          }
        }).then(({ data }: any) => {
          if (data.getImageRecommendations.images.length > 0) {
            const image = data.getImageRecommendations.images[0].id.toString();
            const notLatest = image !== props.job?.image.id ? true : false;
            setExecutionImage({ id: props.job!.image.id, notLatest });
            // sometime the image request isn't done for command so you validate here once again
            (!customCommand || customCommand === undefined) &&
              setCustomCommand(getCommandRecommendation(image));
          } else {
            setExecutionImage({ id: props.job!.image.id });
            // sometime the image request isn't done for command so you validate here once again
            (!customCommand || customCommand === undefined) &&
              setCustomCommand(getCommandRecommendation(props.job!.image.id));
          }
        });
        setCloneAlreadyRecommended(true);
      } else if (
        props.executionFileSource !== "phil" &&
        executionScript.notFound === false &&
        cloneAlreadyRecommended === false
      ) {
        const [repoName, fileName] = getFileNameAndRepoName(
          executionScript.absolutePath
        );

        getExecIEImageRecommendations({
          variables: {
            activityId: activityId,
            environmentId: props.executionFileSource,
            repoName,
            path: fileName
          }
        }).then(({ data }: any) => {
          if (data.getExecutionImageRecommendations.images.length > 0) {
            const image =
              data.getExecutionImageRecommendations.images[0].toString();
            const notLatest = image !== props.job?.image.id ? true : false;
            setExecutionImage({ id: props.job!.image.id, notLatest });
            (!customCommand || customCommand === undefined) &&
              setCustomCommand(getCommandRecommendation(image));
          } else {
            // set whatever image was in original job, if no recommendation image
            setExecutionImage({ id: props.job!.image.id });
            (!customCommand || customCommand === undefined) &&
              setCustomCommand(getCommandRecommendation(props.job!.image.id));
          }
        });
        setCloneAlreadyRecommended(true);
      }
    }
  }, [executionScript]);

  useEffect(() => {
    // revalidate if files are valid when switching between environment source
    if (readyForFetchRepositoryObject) {
      executionScript.absolutePath && fetchScript(tree, executionScript);
      executionInputs.length > 0 && fetchInputs(tree, executionInputs);
    }
  }, [props.executionFileSource, tree]);

  const fetchScript = async (
    repos: RepositoryTree[],
    script: ExtendRepoObjects
  ) => {
    if (isFetchingInputs.current) return;
    let locatedScript = script
      ? await getRepoObjectForExecution(
          props.executionFileSource,
          callRepositoryObjects,
          callSingleRepositoryObject,
          script,
          repos
        )
      : { repositoryId: "", versionId: "", absolutePath: "" };
    setExecutionScript(locatedScript);
  };

  const fetchInputs = async (
    repos: RepositoryTree[],
    inputs: ExtendRepoObjects[]
  ) => {
    setIsLoadingValidateRepositoryObjects(true);

    let locatedInputs: ExtendRepoObjects[] = [];
    const BATCH_SIZE = 5;
    const DELAY_MS = 30000;

    const sleep = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));

    try {
      let i = 0;
      while (i < inputs.length) {
        const batch = inputs.slice(i, i + BATCH_SIZE);
        const batchProsmises = batch.map(async (input: ExtendRepoObjects) => {
          return await getRepoObjectForExecution(
            props.executionFileSource,
            callRepositoryObjects,
            callSingleRepositoryObject,
            input,
            repos
          );
        });

        const batchResults: ExtendRepoObjects[] =
          await Promise.all(batchProsmises);

        locatedInputs.push(...batchResults.flat());

        if (i + BATCH_SIZE < inputs.length) {
          await sleep(DELAY_MS);
        }
        i += BATCH_SIZE;
      }
    } finally {
      setIsLoadingValidateRepositoryObjects(false);
    }

    setExecutionInputs(locatedInputs);
  };

  const loadingMessage = () => {
    if (isLoadingRepositoryObjectsPagination) {
      return "Please wait while we load all files from folder(s)...";
    }
    if (isLoadingValidateRepositoryObjects) {
      return "Please wait while we load and validate all files...";
    }
    if (
      isLoadingExecutionImageRecommendations ||
      isLoadingExecIEImageRecommendations
    ) {
      return "Loading image recommendations...";
    }
    if (
      isLoadingExecutionInputRecommendations ||
      isLoadingExecInputIERecommendations
    ) {
      return "Loading input recommendations...";
    }
    if (isSubmitExecutionJobLoading || isSubmitExecutionJobIELoading) {
      return "Submitting job...";
    }
    return "Loading...";
  };
  return (
    <>
      {isBayerUser && (
        <ErrorBoundary>
          <Container fluid className="modal-75w">
            {isLoading && (
              <Row>
                <div className="center_div">
                  <Spinner animation="border" className="spinner_color" />
                  <p>{loadingMessage()}</p>
                </div>
              </Row>
            )}
            <>
              <div className="h4">
                Create Job{" "}
                <span>
                  <UserManualLink
                    url="/usermanual/execution/#defining-a-job"
                    testId="um-actions"
                  />
                </span>
              </div>

              <hr />
              <FormControl fullWidth disabled={isLoading}>
                {!props.IEEEBridge && (
                  <EnvironmentSelect
                    isLoading={isLoadingConditions.some(
                      (condition) => condition
                    )}
                    executionFileSource={props.executionFileSource}
                    setExecutionFileSource={
                      props.setExecutionFileSource ?? (() => {})
                    }
                    environments={props.interactiveEnvironments || []}
                  />
                )}
                <TextField
                  required
                  disabled={props.executionFileSource === "" || isLoading}
                  id="job-script"
                  label="Script"
                  className="w-100 mt-4"
                  defaultValue={executionScript?.absolutePath}
                  onClick={() =>
                    props.executionFileSource !== "" &&
                    !isLoading &&
                    openModal(FileType.Script)
                  }
                  InputProps={{
                    onKeyDown: (event) => {
                      event.preventDefault();
                    },
                    ...(executionScript?.absolutePath !== "" && {
                      startAdornment: (
                        <>
                          <Chip
                            className="chip"
                            label={executionScript?.absolutePath}
                            disabled={isLoading}
                            onDelete={() => handleDeleteChip("script")}
                            color={
                              executionScript?.notFound ? "error" : "default"
                            }
                          />
                        </>
                      )
                    })
                  }}
                />
                <TextField
                  id="job-input-files"
                  label="Input Files"
                  className="w-100 mt-4"
                  disabled={props.executionFileSource === "" || isLoading}
                  onClick={() =>
                    props.executionFileSource !== "" &&
                    !isLoading &&
                    openModal(FileType.Input)
                  }
                  InputProps={{
                    onKeyDown: (event) => {
                      event.preventDefault();
                    },
                    ...(executionInputs.length > 0 && {
                      startAdornment: (
                        <div className="w-100">
                          {executionInputs.map((x: any, index: number) => (
                            <Chip
                              className="chip my-1"
                              key={index}
                              label={x?.absolutePath}
                              disabled={
                                props.executionFileSource === "" || isLoading
                              }
                              onDelete={() =>
                                handleDeleteChip("input", x?.absolutePath)
                              }
                              color={x?.notFound ? "error" : "default"}
                            />
                          ))}
                        </div>
                      )
                    })
                  }}
                />
                <Grid container spacing={2} className="mt-2">
                  <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                      <InputLabel htmlFor="job-cpus">CPUs</InputLabel>
                      <OutlinedInput
                        label="CPUs"
                        type="number"
                        id="job-cpus"
                        endAdornment={
                          <InputAdornment position="end">CPU(s)</InputAdornment>
                        }
                        onChange={(e) =>
                          Number(e.target.value) > 0 &&
                          Number(e.target.value) <= 96 &&
                          setVCPU(Number(e.target.value))
                        }
                        value={vCPU}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                      <InputLabel htmlFor="job-memory">Memory</InputLabel>
                      <OutlinedInput
                        label="Memory"
                        type="number"
                        id="job-memory"
                        endAdornment={
                          <InputAdornment position="end">GB</InputAdornment>
                        }
                        inputProps={{
                          maxLength: 122.88,
                          step: "0.001"
                        }}
                        onChange={(e) =>
                          Number(e.target.value) > 0.512 &&
                          Number(e.target.value) <= 122.88 &&
                          setMemory(Number(e.target.value))
                        }
                        value={memory}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                      <InputLabel>Nodes</InputLabel>
                      <OutlinedInput
                        disabled={
                          !getImageById(executionImage.id)?.requirements
                            .isMultinode || isLoading
                        }
                        label="Nodes"
                        type="number"
                        id="job-nodes"
                        endAdornment={
                          <InputAdornment position="end">
                            Node(s)
                          </InputAdornment>
                        }
                        onChange={(event: any) => handleOnNodeChange(event)}
                        value={nodes}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth disabled={isLoading}>
                      <InputLabel>Timeout</InputLabel>
                      <OutlinedInput
                        label="Timeout"
                        type="number"
                        endAdornment={
                          <InputAdornment position="end">Day(s)</InputAdornment>
                        }
                        onChange={(e) =>
                          Number(e.target.value) > 0 &&
                          Number(e.target.value) <= 14 &&
                          setExecutionTimeout(Number(e.target.value))
                        }
                        value={executionTimeout}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} className="mt-2">
                  <Grid item xs={6}>
                    <FormControlLabel
                      id="execution-command-input-switch"
                      control={
                        <Switch
                          checked={isCustomCommandVisible}
                          onChange={() => {
                            setIsCustomCommandVisible(!isCustomCommandVisible);
                          }}
                        />
                      }
                      label="Customize Command?"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id="job-working-dir"
                      label="Working Directory"
                      className="w-100"
                      onClick={() =>
                        executionScript?.absolutePath !== "" &&
                        !isLoading &&
                        openModal(FileType.WorkingDir)
                      }
                      disabled={
                        executionScript?.absolutePath === "" || isLoading
                      }
                      InputProps={{
                        onKeyDown: (event) => {
                          event.preventDefault();
                        },
                        startAdornment: (
                          <Chip
                            className="chip"
                            disabled={
                              executionScript?.absolutePath === "" || isLoading
                            }
                            label={executionWorkingDir}
                          />
                        )
                      }}
                    />
                  </Grid>
                </Grid>
                {isCustomCommandVisible && (
                  <TextField
                    required
                    multiline
                    id="execution-command-input"
                    label="Execute Command"
                    variant="outlined"
                    className="mt-4"
                    value={customCommand}
                    disabled={isLoading}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setCustomCommand(event.target.value);
                    }}
                  />
                )}
                <TextField
                  select
                  required
                  id="execution-image"
                  className="my-4"
                  value={executionImage.id}
                  label="Image"
                  disabled={isLoading}
                  onChange={(event) => {
                    handleImageChange(event.target.value);
                  }}
                  sx={{
                    backgroundColor:
                      executionImage.id && executionImage.notLatest
                        ? "#fff4e5"
                        : ""
                  }}
                >
                  {executionImages?.map((image: any) => (
                    <MenuItem value={image.id} key={image.id}>
                      <Row id={`row-image-${image.id}`}>
                        <Typography
                          sx={{ width: "200px" }}
                          variant="button"
                          display="block"
                        >
                          {image.name}
                        </Typography>
                        <div className="d-flex gap-4 fs-12px">
                          <p className="mb-0">
                            Env Type: {image.environmentType}
                          </p>
                          <p className="mb-0">
                            Env Version: {image.environmentTypeVersion}
                          </p>
                          <p className="mb-0">
                            {" "}
                            Validation Type: {image.validationType}
                          </p>
                          <p className="mb-0">
                            Validation Type Version:{" "}
                            {image.validationTypeVersion}
                          </p>
                        </div>
                      </Row>
                    </MenuItem>
                  ))}
                </TextField>

                <h5>Environment Variables</h5>
                <Grid container spacing={2} className="mt-2">
                  <Grid item xs={5}>
                    <TextField
                      id="environmental-variables-name"
                      label="Parameter Name"
                      variant="outlined"
                      className="w-100"
                      value={environmentalVariableName}
                      disabled={isLoading}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setEnvironmentalVariableName(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      id="environmental-variables-value"
                      label="Value"
                      variant="outlined"
                      className="w-100"
                      value={environmentalVariableValue}
                      disabled={isLoading}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setEnvironmentalVariableValue(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={2} className="my-auto">
                    <Button
                      className="w-100"
                      variant="primary"
                      id="add-environmental-variable-entry"
                      disabled={
                        !environmentalVariableName ||
                        !environmentalVariableValue ||
                        isLoading
                      }
                      onClick={addEnvironmentalVariablesEntry}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
                <div className="execution-parameter-chip-holder mt-2">
                  {environmentVariables.map((parameter: any) => (
                    <Chip
                      avatar={
                        <Chip
                          className="avatar-parameter"
                          color="primary"
                          label={parameter.name}
                        />
                      }
                      label={parameter.value}
                      onDelete={() =>
                        handleDeleteEnvironmentalVariableChip(parameter.name)
                      }
                      key={"avatar-parameter" + parameter.name}
                    />
                  ))}
                </div>
                <Grid container spacing={2} className="mt-2">
                  <Grid item xs={6}>
                    <TextField
                      className="w-100"
                      required
                      id="execution-name"
                      label="Job Name"
                      value={executionName}
                      disabled={isLoading}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setExecutionName(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      className="w-100"
                      id="execution-description"
                      label="Description"
                      variant="outlined"
                      value={executionDescription}
                      disabled={isLoading}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setExecutionDescription(event.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} className="mt-2">
                    <TextField
                      select
                      required
                      id="execution-action"
                      value={getActionTypeKeyFromValue(executionActionType)}
                      label="Action Type"
                      className="w-100"
                      disabled={isLoading}
                      onChange={(event) => {
                        setExecutionActionType(
                          ActionTypeUI[
                            event.target.value as keyof typeof ActionTypeUI
                          ]
                        );
                      }}
                    >
                      {Object.entries(ActionTypeUI)
                        .filter((element) =>
                          Object.values(ExecutionActionType).includes(
                            element[0] as any
                          )
                        )
                        .map((element) => (
                          <MenuItem key={element[0]} value={element[0]}>
                            {element[1]}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} className="mt-2">
                    <TextField
                      select
                      required
                      id="execution-quality"
                      value={String(
                        getQualityCheckStatusKeyFromValue(qualityCheckStatus)
                      )}
                      label="Quality Control Status"
                      className="w-100"
                      disabled={isLoading}
                      onChange={(event) => {
                        setQualityCheckStatus(
                          QualityCheckStatusUI[
                            event.target.value as QualityCheckStatusType
                          ]
                        );
                      }}
                    >
                      {Object.entries(QualityCheckStatusUI).map((element) => (
                        <MenuItem key={element[0]} value={element[0]}>
                          {element[1]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <div className="mt-4 d-flex justify-content-end w-100">
                  <div className="d-flex justify-content-end w-50">
                    <Button
                      variant="secondary"
                      id="job-reset-new-button"
                      className="w-25 me-2"
                      onClick={resetJob}
                      disabled={isSubmitExecutionJobLoading || isLoading}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      id="job-create-new-button"
                      className="w-25"
                      onClick={createExecutionJob}
                      disabled={disableCreateButton()}
                    >
                      Create
                    </Button>
                  </div>
                </div>
              </FormControl>
            </>

            <FileSelectModal
              setModalAction={setModalConfig}
              updateFileSelection={updateFileSelectionTemporary}
              onConfirmModal={onConfirmModal}
              modelConfig={modalConfig}
              selectedFile={getFileSelection()}
              isFileHidden={isFileHiddenInTreeView}
              treeSelection={tree}
              parentLoading={props.parentLoading}
            />
          </Container>
        </ErrorBoundary>
      )}

      {error && <Error error={error} />}
      {successMessage && (
        <Success message="Interactive Execution Bridge Job Created. It can take up to 15 Minutes to appear." />
      )}
    </>
  );
}
